import React, { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Building2, Eye, Plus, Users } from 'lucide-react';

import {
	useCreateEnterpriseMutation,
	useListEnterprisesQuery,
} from '../api/admin';
import {
	Button,
	Input,
	Modal,
	ModalBody,
	ModalFooter,
	PageContent,
	PageHeader,
	SearchInput,
	SelectInput,
	SkeletonTable,
	StatusBadge,
	Typography,
} from '../components/common';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../providers/AppAuthProvider';
import {
	getDateFilterLabel,
	getDateRangeForFilter,
} from '../utils/dateFilters';
import { hasAnyRole } from '../utils/utils';

import type { Enterprise } from '../api/admin';
import type { DateFilterPreset } from '../utils/dateFilters';

const AdminPage: React.FC = () => {
	const [allEnterprises, setAllEnterprises] = useState<Enterprise[]>([]);
	const [filteredEnterprises, setFilteredEnterprises] = useState<Enterprise[]>(
		[],
	);
	const [isAuthorized, setIsAuthorized] = useState(false);
	const [activeOnly, setActiveOnly] = useState<boolean>(false);
	const [dateFilter, setDateFilter] =
		useState<DateFilterPreset>('last_30_days');
	const [searchQuery, setSearchQuery] = useState<string>('');
	const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [newEnterpriseName, setNewEnterpriseName] = useState('');
	const [newEnterpriseEmail, setNewEnterpriseEmail] = useState('');
	const [newEnterprisePhone, setNewEnterprisePhone] = useState('');
	const [newEnterpriseDescription, setNewEnterpriseDescription] = useState('');
	const [activeTab, setActiveTab] = useState<'enterprises' | 'api-keys'>(
		'enterprises',
	);

	const { user } = useAuth();
	const { showToast } = useToast();
	const navigate = useNavigate();

	// Check if user can view enterprise details (computed once, not in render loop)
	const canViewEnterpriseDetails = React.useMemo(() => {
		return hasAnyRole(user?.roles, ['super_admin', 'tenant_admin']);
	}, [user?.roles]);

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchQuery(searchQuery);
		}, 300);

		return () => clearTimeout(timer);
	}, [searchQuery]);

	// Check if user has required role (super_admin, tenant_admin, or enterprise_admin)
	useEffect(() => {
		if (user?.roles) {
			const hasRequiredRole = hasAnyRole(user.roles, [
				'super_admin',
				'tenant_admin',
				'enterprise_admin',
			]);
			setIsAuthorized(hasRequiredRole);

			if (!hasRequiredRole) {
				showToast(
					'Access Denied',
					'You do not have permission to access this page',
					'error',
				);
				navigate('/');
			}
		}
	}, [user, navigate, showToast]);

	// Calculate date range from filter preset
	const dateRange = getDateRangeForFilter(dateFilter);

	// API hooks
	const {
		data: enterprisesData,
		isLoading,
		isFetching,
		refetch,
	} = useListEnterprisesQuery({
		active_only: activeOnly,
		last_login_since: dateRange.since,
		last_login_before: dateRange.before,
	});

	const [createEnterprise, { isLoading: creatingEnterprise }] =
		useCreateEnterpriseMutation();

	// Refetch when filter changes
	useEffect(() => {
		refetch();
	}, [activeOnly, dateFilter, refetch]);

	// Update enterprises list when data changes
	useEffect(() => {
		if (enterprisesData?.enterprises) {
			setAllEnterprises(enterprisesData.enterprises);
		}
	}, [enterprisesData]);

	// Filter enterprises based on search
	useEffect(() => {
		let filtered = allEnterprises;

		if (debouncedSearchQuery.trim()) {
			const query = debouncedSearchQuery.toLowerCase();
			filtered = filtered.filter(
				(enterprise) =>
					enterprise.company_name.toLowerCase().includes(query) ||
					enterprise.enterprise_id.toLowerCase().includes(query) ||
					(enterprise.contact_email &&
						enterprise.contact_email.toLowerCase().includes(query)),
			);
		}

		setFilteredEnterprises(filtered);
	}, [allEnterprises, debouncedSearchQuery]);

	const getStatusBadge = (active: boolean) => {
		return active ? (
			<StatusBadge status="active">Active</StatusBadge>
		) : (
			<StatusBadge status="inactive">Inactive</StatusBadge>
		);
	};

	const formatLastLogin = (dateString?: string | null) => {
		if (!dateString) return 'Never';
		try {
			const date = new Date(dateString);
			const now = new Date();
			const diffMs = now.getTime() - date.getTime();
			const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

			if (diffDays === 0) {
				const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
				if (diffHours === 0) {
					const diffMinutes = Math.floor(diffMs / (1000 * 60));
					return diffMinutes <= 1 ? 'Just now' : `${diffMinutes} minutes ago`;
				}
				return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
			} else if (diffDays === 1) {
				return 'Yesterday';
			} else if (diffDays < 7) {
				return `${diffDays} days ago`;
			} else {
				return date.toLocaleDateString();
			}
		} catch {
			return dateString;
		}
	};

	if (!isAuthorized) {
		return null;
	}

	return (
		<div className="flex flex-1 flex-col">
			<PageHeader
				title="Organization Management"
				variant="title"
				actions={
					activeTab === 'enterprises' ? (
						<Button
							onClick={() => setShowCreateModal(true)}
							variant="primary"
							icon={Plus}
						>
							Create Organization
						</Button>
					) : null
				}
			/>

			<PageContent className="max-w-[1280px] m-auto mb-20">
				<div className="flex border-b border-gray-200 mb-6">
					<button
						className={`px-6 py-3 text-sm font-medium transition-colors relative focus:outline-none ${
							activeTab === 'enterprises'
								? 'text-primary-600'
								: 'text-gray-500 hover:text-gray-700'
						}`}
						onClick={() => setActiveTab('enterprises')}
					>
						Organizations
						{activeTab === 'enterprises' && (
							<div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600" />
						)}
					</button>
				</div>

				<div className="w-full bg-white border border-gray-200 rounded-lg">
					<div className="flex items-center justify-between mb-6 p-6">
						<Typography
							variant="body1"
							className="text-lg font-normal text-gray-900"
						>
							All Organizations ({filteredEnterprises.length})
						</Typography>
						<div className="flex items-center gap-4">
							{/* Search */}
							<div className="relative">
								<SearchInput
									placeholder="Search organizations..."
									value={searchQuery}
									onChange={(value) => {
										setSearchQuery(value);
									}}
									className="pl-10 w-64"
								/>
								{isFetching && searchQuery && (
									<div className="absolute inset-y-0 right-3 flex items-center">
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
									</div>
								)}
							</div>

							{/* Filter Dropdown */}
							<SelectInput
								value={activeOnly ? 'active' : 'all'}
								onChange={(value) => {
									setActiveOnly(value === 'active');
								}}
								options={[
									{ value: 'all', label: 'All Organizations' },
									{ value: 'active', label: 'Active Only' },
								]}
								fullWidth={false}
								className="w-40"
							/>

							{/* Date Filter Dropdown */}
							<SelectInput
								value={dateFilter}
								onChange={(value) => {
									setDateFilter(value as DateFilterPreset);
								}}
								options={[
									{
										value: 'last_30_days',
										label: getDateFilterLabel('last_30_days'),
									},
									{
										value: 'this_month',
										label: getDateFilterLabel('this_month'),
									},
									{
										value: 'last_month',
										label: getDateFilterLabel('last_month'),
									},
									{
										value: 'this_quarter',
										label: getDateFilterLabel('this_quarter'),
									},
									{
										value: 'last_quarter',
										label: getDateFilterLabel('last_quarter'),
									},
								]}
								fullWidth={false}
								className="w-40"
							/>
						</div>
					</div>

					{isLoading || isFetching ? (
						<div className="w-full">
							<SkeletonTable rows={10} columns={6} showHeader={true} />
						</div>
					) : (
						<div className="overflow-x-auto">
							<table className="min-w-full divide-y divide-gray-200">
								<thead className="bg-gray-50">
									<tr>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Company Name
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Users
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Last Login
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Status
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Actions
										</th>
									</tr>
								</thead>
								<tbody className="bg-white divide-y divide-gray-200">
									{filteredEnterprises.map((enterprise) => (
										<tr key={enterprise.id} className="hover:bg-gray-50">
											<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
												<div className="flex items-center gap-2">
													<Building2 className="w-4 h-4 text-gray-400" />
													{enterprise.company_name}
												</div>
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
												<div className="flex items-center gap-1">
													<Users className="w-4 h-4 text-gray-400" />
													{enterprise.user_count}
												</div>
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
												{formatLastLogin(enterprise.last_login_at)}
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												{getStatusBadge(enterprise.active)}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
												{canViewEnterpriseDetails ? (
													<Button
														onClick={() =>
															navigate(
																`/admin/enterprises/${enterprise.enterprise_id}`,
															)
														}
														variant="outline"
														size="xs"
														className="text-primary-600 hover:text-primary-900"
														icon={Eye}
													>
														View Details
													</Button>
												) : (
													<span className="text-gray-400 text-xs">
														No access
													</span>
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>

							{filteredEnterprises.length === 0 && (
								<div className="text-center py-8">
									<p className="text-gray-500">
										No organizations found matching your criteria.
									</p>
								</div>
							)}
						</div>
					)}
				</div>
			</PageContent>

			{/* Create Enterprise Modal */}
			<Modal
				isOpen={showCreateModal}
				onClose={() => {
					setShowCreateModal(false);
					setNewEnterpriseName('');
					setNewEnterpriseEmail('');
					setNewEnterprisePhone('');
					setNewEnterpriseDescription('');
				}}
				title="Create New Organization"
				size="md"
			>
				<ModalBody>
					<div className="space-y-4">
						<div>
							<Typography variant="body2" className="mb-1">
								Company Name *
							</Typography>
							<Input
								type="text"
								value={newEnterpriseName}
								onChange={(e) => setNewEnterpriseName(e.target.value)}
								placeholder="Enter company name"
								fullWidth
							/>
						</div>
						<div>
							<Typography variant="body2" className="mb-1">
								Contact Email
							</Typography>
							<Input
								type="email"
								value={newEnterpriseEmail}
								onChange={(e) => setNewEnterpriseEmail(e.target.value)}
								placeholder="contact@example.com"
								fullWidth
							/>
						</div>
						<div>
							<Typography variant="body2" className="mb-1">
								Contact Phone
							</Typography>
							<Input
								type="tel"
								value={newEnterprisePhone}
								onChange={(e) => setNewEnterprisePhone(e.target.value)}
								placeholder="+1234567890"
								fullWidth
							/>
						</div>
						<div>
							<Typography variant="body2" className="mb-1">
								Description
							</Typography>
							<Input
								type="text"
								value={newEnterpriseDescription}
								onChange={(e) => setNewEnterpriseDescription(e.target.value)}
								placeholder="Organization description"
								fullWidth
							/>
						</div>
					</div>
				</ModalBody>
				<ModalFooter>
					<Button
						onClick={() => {
							setShowCreateModal(false);
							setNewEnterpriseName('');
							setNewEnterpriseEmail('');
							setNewEnterprisePhone('');
							setNewEnterpriseDescription('');
						}}
						variant="secondary"
					>
						Cancel
					</Button>
					<Button
						onClick={async () => {
							if (!newEnterpriseName) {
								showToast('Error', 'Company name is required', 'error');
								return;
							}
							try {
								await createEnterprise({
									company_name: newEnterpriseName,
									contact_email: newEnterpriseEmail || undefined,
									contact_phone: newEnterprisePhone || undefined,
									description: newEnterpriseDescription || undefined,
									active: true,
								}).unwrap();
								showToast(
									'Success',
									'Organization created successfully',
									'success',
								);
								setShowCreateModal(false);
								setNewEnterpriseName('');
								setNewEnterpriseEmail('');
								setNewEnterprisePhone('');
								setNewEnterpriseDescription('');
								refetch();
							} catch (error: any) {
								const errorMessage =
									error?.data?.message ||
									error?.message ||
									'Failed to create organization';
								showToast('Error', errorMessage, 'error');
							}
						}}
						variant="primary"
						loading={creatingEnterprise}
						disabled={!newEnterpriseName}
					>
						Create Organization
					</Button>
				</ModalFooter>
			</Modal>
		</div>
	);
};

export default AdminPage;
